/**
 * VqeStatementBody — the statement view's body: dispatches the classified
 * statement to its renderer (SELECT / INSERT / UPDATE / DELETE / MERGE, a
 * drilled subquery, the read-only / parse-error fallbacks), or the starter
 * canvas when the buffer has no statement.
 */
import type { ParseOptions } from '../../../sql-grammar/model';
import type { SqlResolverService } from '../../../sql-resolver/service/resolverService';
import type { VqeEditContext } from '../edit/editContext';
import { DeleteRenderer } from '../render/delete/DeleteRenderer';
import { ParseErrorFallback } from '../render/fallback/ParseErrorFallback';
import { ReadOnlyFallback, type ReadOnlyFallbackReason } from '../render/fallback/ReadOnlyFallback';
import { InsertRenderer } from '../render/insert/InsertRenderer';
import { MergeRenderer } from '../render/merge/MergeRenderer';
import { SelectRenderer } from '../render/select/SelectRenderer';
import { UpdateRenderer } from '../render/update/UpdateRenderer';
import type { VqeReadinessState } from '../ux/VqeReadinessBanner';
import { FALLBACK_REASONS, type ClassificationResult } from './classifyTabQuery';
import type { VqeTabDrill } from './useVqeTabDrill';
import { VqeStarterCanvas } from './VqeStarterCanvas';

export type VqeStatementBodyProps = {
  readonly tabId: string;
  readonly classification: ClassificationResult | null;
  readonly options: ParseOptions;
  readonly editContext: VqeEditContext | null;
  readonly service: SqlResolverService;
  readonly drill: VqeTabDrill;
  readonly connectionId?: string;
  readonly database?: string;
  readonly readinessState: VqeReadinessState;
  readonly onEditAsSql: (errorOffset: number | null) => void;
  readonly onReloadSchema: () => void;
  readonly onChooseDatabase?: () => void;
  readonly onSeedQuery: (sql: string) => void;
};

export function VqeStatementBody({
  tabId,
  classification,
  options,
  editContext,
  service,
  drill,
  connectionId,
  database,
  readinessState,
  onEditAsSql,
  onReloadSchema,
  onChooseDatabase,
  onSeedQuery,
}: VqeStatementBodyProps) {
  const statement = classification?.statement ?? null;
  const mode = classification?.renderMode;
  const editAsSql = () => onEditAsSql(null);

  if (classification && mode === 'parse-error') {
    return (
      <ParseErrorFallback
        statement={statement}
        recovery={classification.buffer.parsedDocument.recovery}
        bufferText={classification.buffer.parsedDocument.source}
        onEditAsSql={(errorOffset) => onEditAsSql(errorOffset ?? null)}
        dialect={options.engine}
      />
    );
  }
  const canvas = (
    <VqeStarterCanvas
      service={service}
      engine={options.engine}
      readinessState={readinessState}
      onStartFromSql={editAsSql}
      onSeedQuery={onSeedQuery}
      onRefreshSchema={onReloadSchema}
      // SQLite has one database per connection — nothing to switch to.
      onChooseDatabase={options.engine === 'sqlite' ? undefined : onChooseDatabase}
    />
  );
  if (!classification || !statement || !mode) return canvas;

  const bufferText = classification.buffer.parsedDocument.source;
  const common = { bufferText, options, onEditAsSql: editAsSql, drillTabId: tabId };
  if (FALLBACK_REASONS.has(mode)) {
    return (
      <ReadOnlyFallback
        statement={statement}
        reason={mode as ReadOnlyFallbackReason}
        bufferText={bufferText}
        onEditAsSql={editAsSql}
        dialect={options.engine}
        connectionId={connectionId}
        database={database}
      />
    );
  }
  // A drilled SELECT or INSERT…SELECT source renders as a top-level SELECT.
  if ((mode === 'select' || mode === 'insert') && (mode === 'select' || drill.drilledStatement)) {
    return (
      <SelectRenderer
        {...common}
        statement={drill.drilledStatement ?? statement}
        editContext={(drill.drilledEditContext ?? editContext) ?? undefined}
        outerAliases={drill.outerAliases}
      />
    );
  }
  const context = editContext ?? undefined;
  if (mode === 'insert') return <InsertRenderer {...common} statement={statement} editContext={context} />;
  if (mode === 'update') return <UpdateRenderer {...common} statement={statement} editContext={context} />;
  if (mode === 'delete') return <DeleteRenderer {...common} statement={statement} editContext={context} />;
  if (mode === 'merge') return <MergeRenderer {...common} statement={statement} editContext={context} />;
  return canvas;
}
